import React from "react";
import { ExternalLink } from "lucide-react";
import { formatCOP } from "../../lib/apiClient";
import { SecopSearchTrace, StatusPill } from "./SecopSearchTrace";

function EmptyState({ search }) {
  const st = search?.overall_status;
  const msg = !search
    ? "Aún no se ha realizado la búsqueda de comparables para este caso."
    : st === "consulta_fallida" || st === "parcial_con_fallos"
      ? "La consulta a SECOP falló. Esto no significa que no existan procesos similares; el revisor puede repetirla."
      : "Sin resultados con estos filtros (palabras clave del objeto, territorio y valor registrado).";
  return <p className="text-sm text-gray-600 py-3" data-testid="comparables-empty">{msg}</p>;
}

export function ComparablesTable({ items = [], search }) {
  return (
    <div data-testid="comparables-section">
      {search && (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-gray-600">
          <StatusPill status={search.overall_status} testId="comparables-overall-status" />
          <span>Departamento del caso: <strong>{search.department || "no indicado"}</strong></span>
        </div>
      )}
      {items.length === 0 ? <EmptyState search={search} /> : (
        <div className="overflow-x-auto border border-gray-200">
          <table className="w-full text-xs" data-testid="comparables-table">
            <thead className="bg-gray-50">
              <tr className="text-[10px] uppercase tracking-widest text-gray-500">
                <th className="text-left p-2">Objeto</th>
                <th className="text-left p-2">Entidad · territorio</th>
                <th className="text-right p-2">Valor</th>
                <th className="text-left p-2">Modalidad</th>
                <th className="text-left p-2">Adjudicatario</th>
                <th className="text-left p-2">Fuente</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c, i) => (
                <tr key={c.id || i} className="border-t border-gray-100 align-top hover:bg-gray-50/60 transition-colors" data-testid={`comparable-row-${i}`}>
                  <td className="p-2 text-gray-800 max-w-[320px]"><span className="line-clamp-3" title={c.objeto}>{c.objeto}</span></td>
                  <td className="p-2 text-gray-700">
                    {c.entidad}
                    {c.territorio && <span className="block text-gray-500">{c.territorio}</span>}
                    {c.alcance_busqueda && <span className="block text-[10px] uppercase tracking-wide text-primary/80">{c.alcance_busqueda}</span>}
                  </td>
                  <td className="p-2 text-right font-mono whitespace-nowrap" data-testid={`comparable-value-${i}`}>{c.valor_cop ? formatCOP(c.valor_cop) : "—"}</td>
                  <td className="p-2 text-gray-700">{c.modalidad || "—"}</td>
                  <td className="p-2 text-gray-700" data-testid={`comparable-adjudicatario-${i}`}>{c.adjudicatario || "No registrado"}</td>
                  <td className="p-2 whitespace-nowrap">
                    <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`comparable-link-${i}`}>
                      {c.plataforma || "SECOP"} <ExternalLink className="w-3 h-3" />
                    </a>
                    {c.fecha && <span className="block text-gray-400">{c.fecha}</span>}
                    {c.manual && <span className="block text-[10px] text-amber-700">Manual · no verificada</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {search?.steps?.length > 0 && (
        <details className="mt-3" data-testid="comparables-trace-details">
          <summary className="text-xs text-gray-500 cursor-pointer hover:text-primary">Cómo se buscaron (secuencia y criterios)</summary>
          <SecopSearchTrace steps={search.steps} />
          <ul className="mt-2 text-[11px] text-gray-500 list-disc pl-5">
            {(search.similarity_criteria || []).map((c) => <li key={c}>{c}</li>)}
            {search.neighbor_criterion && <li>Vecinos: {search.neighbor_criterion}</li>}
          </ul>
        </details>
      )}
    </div>
  );
}
